import { MessageCircle, Pencil, Plus, Search, Upload, Users } from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import CustomerFormModal from "@/components/CustomerFormModal";
import ImportCustomersModal from "@/components/ImportCustomersModal";
import { Badge, Button, Card, EmptyState, Input, Spinner } from "@/components/ui";

import { useFormat, useT } from "@/lib/i18n";
import { useCustomers, useMe } from "@/lib/queries";
import type { Customer, Me } from "@/lib/types";

export default function Customers() {
  const t = useT();
  const { money, phone } = useFormat();
  const { region } = useOutletContext<Me>().garage;
  const [search, setSearch] = useState("");
  const [importando, setImportando] = useState(false);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<Customer | null>(null);
  const { data, isLoading, isFetching } = useCustomers(search);
  // Mesma permissão que o backend cobra na rota (IsAdmin): mostrar o botão
  // para o profissional só renderia um 403.
  const podeImportar = useMe().data?.permissions.is_admin ?? false;

  const botaoImportar = (
    <Button variant="secondary" onClick={() => setImportando(true)}>
      <Upload size={16} />{t("customers.importCsv")}</Button>
  );

  // Sem trava de papel: a rota cobra `IsMember`, e quem cadastra cliente no
  // balcão é a recepção. A importação é que fica no admin, porque mexe na
  // base inteira de uma vez.
  const botaoNovo = (
    <Button variant="primary" onClick={() => setCriando(true)}>
      <Plus size={16} />{t("modal.newCustomer")}</Button>
  );

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold lg:text-2xl">{t("customers.title")}</h1>
          <p className="text-sm text-steel-muted">
            {data?.count ?? 0} cliente(s) cadastrado(s)
          </p>
        </div>
        <div className="flex gap-2">
          {podeImportar && botaoImportar}
          {botaoNovo}
        </div>
      </div>

      <div className="relative mb-4">
        <Search
          size={18}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-steel-dim"
        />
        <Input
          placeholder={t("customers.search")}
          className="pl-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isFetching && !isLoading && (
          <Spinner className="absolute top-1/2 right-3 size-4 -translate-y-1/2" />
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : data?.results.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users size={40} />}
            title={search ? t("customers.noneFound") : t("customers.noneYet")}
            description={
              search
                ? t("customers.tryAnother")
                : t("customers.emptyText")
            }
            // Base vazia é exatamente quando a importação existe para ser
            // usada — é o primeiro dia da oficina no sistema.
            action={
              search ? undefined : (
                <div className="flex flex-wrap justify-center gap-2">
                  {podeImportar && botaoImportar}
                  {botaoNovo}
                </div>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {data?.results.map((c) => (
            <Card key={c.id} className="flex items-center gap-3 p-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium">{c.name}</span>
                  {c.is_blocked && (
                    <Badge className="border-red-900/60 bg-red-950/50 text-red-300">{t("common.blocked")}</Badge>
                  )}
                  {c.is_risky && !c.is_blocked && (
                    <Badge className="border-amber-700/50 bg-amber-950/50 text-amber-300">
                      {c.no_show_count} faltas
                    </Badge>
                  )}
                </div>
                <a
                  href={`https://wa.me/${c.phone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 inline-flex items-center gap-1 text-sm text-emerald-400 hover:underline"
                >
                  <MessageCircle size={14} />
                  {phone(c.phone, region.country)}
                </a>
                {c.vehicles.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {c.vehicles.map((v) => (
                      <span key={v.id} className="plate" title={v.label}>
                        {v.formatted_plate}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="text-right text-sm">
                <p className="font-medium">{c.total_visits} visita(s)</p>
                <p className="text-steel-muted">{money(c.total_spent)}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                aria-label={t("common.editOf", { name: c.name })}
                onClick={() => setEditando(c)}
              >
                <Pencil size={14} />
              </Button>
            </Card>
          ))}
        </div>
      )}

      <ImportCustomersModal open={importando} onClose={() => setImportando(false)} />
      <CustomerFormModal
        open={criando || editando !== null}
        customer={editando}
        onClose={() => {
          setCriando(false);
          setEditando(null);
        }}
      />
    </div>
  );
}
